import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Gift, CreditCard, Check, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INPUT_CLS = 'bg-[var(--bg-input)] border-[var(--border-input)] text-[var(--text-primary)] placeholder:text-[var(--text-faint)]';

// Self-contained gift-card section + purchase dialog + Stripe return handling.
// Reused by the menu / business-card / reservations public pages. Payment runs
// through KoLV (Stripe) with the platform 10% commission; a code is emailed on
// success. Renders nothing structural when there are no cards configured.
export default function BusinessGiftCards({ userId, pageId, cards = [], order = 99, pv = {} }) {
  const [sp, setSp] = useSearchParams();
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftIdx, setGiftIdx] = useState(null);
  const [giftResult, setGiftResult] = useState(null);
  const [giftForm, setGiftForm] = useState({ amount: '', buyer_email: '', recipient_email: '', recipient_name: '', message: '' });
  const [giftSubmitting, setGiftSubmitting] = useState(false);
  const [giftChecking, setGiftChecking] = useState(false);

  // Returned from Stripe after a gift-card purchase: poll, then reveal the code.
  useEffect(() => {
    const sid = sp.get('session_id');
    if (sp.get('gift_cancelled')) {
      toast.error('Maksājums atcelts');
      sp.delete('gift_cancelled'); setSp(sp, { replace: true });
      return;
    }
    if (!sid || !sp.get('gift_paid')) return;
    setGiftChecking(true);
    let attempt = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/business-page/gift-card/status/${sid}`);
        if (data.payment_status === 'paid') {
          setGiftChecking(false);
          setGiftResult({ code: data.code, amount: data.amount, recipient_email: data.recipient_email });
          setGiftOpen(true);
          toast.success('Dāvanu karte apmaksāta! Kods nosūtīts e-pastā.');
          sp.delete('gift_paid'); sp.delete('session_id'); setSp(sp, { replace: true });
          return;
        }
      } catch { /* keep polling */ }
      attempt += 1;
      if (attempt >= 8) {
        setGiftChecking(false);
        toast.message('Maksājums tiek apstrādāts', { description: 'Kods tiks nosūtīts e-pastā drīzumā.' });
        sp.delete('gift_paid'); sp.delete('session_id'); setSp(sp, { replace: true });
        return;
      }
      setTimeout(poll, 1500);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const openGift = (idx = null) => {
    setGiftIdx(idx);
    setGiftResult(null);
    const preset = idx != null && cards?.[idx] ? String(cards[idx].amount) : '';
    setGiftForm({ amount: preset, buyer_email: '', recipient_email: '', recipient_name: '', message: '' });
    setGiftOpen(true);
  };

  const submitGift = async () => {
    const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (!emailRe.test(giftForm.buyer_email.trim())) { toast.error('Ievadi derīgu pircēja e-pastu'); return; }
    if (!emailRe.test(giftForm.recipient_email.trim())) { toast.error('Ievadi derīgu saņēmēja e-pastu'); return; }
    const amt = giftIdx != null ? Number(cards[giftIdx].amount) : Number(giftForm.amount);
    if (giftIdx == null && (!amt || amt < 1 || amt > 1000)) { toast.error('Summai jābūt no €1 līdz €1000'); return; }
    setGiftSubmitting(true);
    try {
      const { data: res } = await axios.post(`${API}/business-page/${userId}/gift-card/purchase`, {
        page_id: pageId || undefined,
        card_index: giftIdx != null ? giftIdx : undefined,
        amount: giftIdx == null ? amt : undefined,
        buyer_email: giftForm.buyer_email.trim(),
        recipient_email: giftForm.recipient_email.trim(),
        recipient_name: giftForm.recipient_name.trim() || undefined,
        message: giftForm.message.trim() || undefined,
        origin_url: window.location.origin + window.location.pathname,
      });
      if (res.checkout_url) { window.location.href = res.checkout_url; return; }
      toast.error('Neizdevās sākt maksājumu');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Neizdevās sākt maksājumu');
    } finally { setGiftSubmitting(false); }
  };

  return (
    <>
      <section data-testid="giftcards-section" style={{ order }}>
        <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5" style={{ fontFamily: 'Outfit' }}>Dāvanu kartes</h2>
        <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:gap-4">
          {(cards || []).map((g, i) => (
            <div key={i} className="rounded-2xl p-5 w-full sm:w-auto sm:min-w-[210px] relative overflow-hidden border border-[var(--accent-border)] shadow-sm flex flex-col"
              style={{ background: 'linear-gradient(135deg, var(--accent-soft), transparent)' }} data-testid={`giftcard-${i}`}>
              <Gift className="w-6 h-6 text-[var(--accent)] mb-3" />
              <p className="text-[var(--text-primary)] font-semibold">{g.name}</p>
              <p className="text-2xl font-light text-[var(--accent)] mt-1 mb-4">€{g.amount}</p>
              <button onClick={() => openGift(i)}
                className="mt-auto inline-flex items-center justify-center gap-2 h-10 px-5 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] text-sm font-semibold hover:bg-[var(--accent-hover)] transition-colors"
                data-testid={`giftcard-buy-${i}`}>
                <Gift className="w-4 h-4" /> Pirkt
              </button>
            </div>
          ))}
          {/* Custom amount */}
          <button onClick={() => openGift(null)}
            className="rounded-2xl p-5 w-full sm:w-auto sm:min-w-[210px] border border-dashed border-[var(--accent-border)] bg-[var(--bg-card)] hover:bg-[var(--accent-soft)] transition-colors text-left flex flex-col"
            data-testid="giftcard-custom">
            <Gift className="w-6 h-6 text-[var(--accent)] mb-3" />
            <p className="text-[var(--text-primary)] font-semibold">Cita summa</p>
            <p className="text-sm text-[var(--text-faint)] mt-1 mb-4">Izvēlies dāvanas vērtību pats</p>
            <span className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-[var(--accent)]">
              <CreditCard className="w-4 h-4" /> Pirkt dāvanu karti
            </span>
          </button>
        </div>
      </section>

      {giftChecking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm" style={{ background: 'var(--bg-overlay)' }} data-testid="gift-checking">
          <div className="flex flex-col items-center gap-3 text-[var(--text-primary)]">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--accent)]" />
            <p className="text-sm">Apstiprinām maksājumu…</p>
          </div>
        </div>
      )}

      <Dialog open={giftOpen} onOpenChange={setGiftOpen}>
        <DialogContent style={{ ...pv, background: 'var(--bg-surface-secondary)', borderColor: 'var(--border-card)' }} className="text-[var(--text-primary)] max-h-[88vh] overflow-y-auto w-[92vw] sm:w-full max-w-md" data-testid="gift-dialog">
          {giftResult ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Check className="w-5 h-5 text-[var(--accent)]" /> Dāvanu karte apmaksāta!</DialogTitle>
                <DialogDescription className="text-[var(--text-faint)]">Kods ir nosūtīts uz {giftResult.recipient_email}.</DialogDescription>
              </DialogHeader>
              <div className="rounded-2xl p-6 text-center border border-[var(--accent-border)]" style={{ background: 'linear-gradient(135deg, var(--accent-soft), transparent)' }}>
                <Gift className="w-8 h-8 text-[var(--accent)] mx-auto mb-2" />
                <p className="text-sm text-[var(--text-faint)]">Dāvanu kartes vērtība</p>
                <p className="text-3xl font-light text-[var(--accent)] mb-3">€{Number(giftResult.amount).toFixed(2)}</p>
                <p className="text-sm text-[var(--text-faint)]">Kods</p>
                <p className="text-2xl font-mono font-bold tracking-widest text-[var(--text-primary)]" data-testid="gift-result-code">{giftResult.code}</p>
              </div>
              {giftResult.code && (
                <a href={`/davanu-karte/${giftResult.code}`} target="_blank" rel="noreferrer"
                  className="mt-3 inline-flex items-center justify-center gap-2 h-11 w-full rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] transition-colors" data-testid="gift-view-link">
                  <Gift className="w-4 h-4" /> Apskatīt un izdrukāt dāvanu karti
                </a>
              )}
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Gift className="w-5 h-5 text-[var(--accent)]" /> Pirkt dāvanu karti</DialogTitle>
                <DialogDescription className="text-[var(--text-faint)]">
                  {giftIdx != null && cards?.[giftIdx]
                    ? `${cards[giftIdx].name} — €${Number(cards[giftIdx].amount).toFixed(2)}`
                    : 'Izvēlies summu un ievadi saņēmēja e-pastu — kods tiks nosūtīts pēc apmaksas.'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {giftIdx == null && (
                  <div>
                    <label className="text-xs text-[var(--text-faint)]">Summa (€1–€1000)</label>
                    <Input type="number" min="1" max="1000" value={giftForm.amount}
                      onChange={(e) => setGiftForm({ ...giftForm, amount: e.target.value })}
                      placeholder="Piem. 25" className={INPUT_CLS} data-testid="gift-amount" />
                  </div>
                )}
                <Input type="email" value={giftForm.buyer_email} onChange={(e) => setGiftForm({ ...giftForm, buyer_email: e.target.value })} placeholder="Tavs e-pasts (čekam) *" className={INPUT_CLS} data-testid="gift-buyer-email" />
                <Input type="email" value={giftForm.recipient_email} onChange={(e) => setGiftForm({ ...giftForm, recipient_email: e.target.value })} placeholder="Saņēmēja e-pasts (kur sūtīt kodu) *" className={INPUT_CLS} data-testid="gift-recipient-email" />
                <Input value={giftForm.recipient_name} onChange={(e) => setGiftForm({ ...giftForm, recipient_name: e.target.value })} placeholder="Saņēmēja vārds (neobligāti)" className={INPUT_CLS} data-testid="gift-recipient-name" />
                <Textarea value={giftForm.message} onChange={(e) => setGiftForm({ ...giftForm, message: e.target.value })} placeholder="Personīga ziņa (neobligāti)" className={INPUT_CLS} data-testid="gift-message" />
                <button onClick={submitGift} disabled={giftSubmitting}
                  className="w-full h-11 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="gift-submit">
                  {giftSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  Maksāt {giftIdx != null && cards?.[giftIdx] ? `€${Number(cards[giftIdx].amount).toFixed(2)}` : (giftForm.amount ? `€${Number(giftForm.amount).toFixed(2)}` : '')}
                </button>
                <p className="text-[11px] text-[var(--text-faint)] text-center">Maksājums tiek apstrādāts droši caur KoLV (Stripe).</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
